<script lang="ts">
export default {
  name: 'ExportMenu',
};
</script>

<script setup lang="ts">
import { Download } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import Toggle from '@/components/v2/Toggle.vue';

const props = withDefaults(
  defineProps<{
    items: string[];
    supportIndividualExports?: boolean;
    disabled?: boolean;
  }>(),
  {
    disabled: false
  });

const emit = defineEmits<{
  (e: 'export', type: string, selected: string[], finishExport: () => void, oneFile?: boolean): void;
}>();

const selected = ref<string[]>([]);
const menuOpen = ref<boolean>(false);
const isExporting = ref<boolean>(false);
const oneFile = ref<boolean>(false);

const exportDisabled = computed(() => selected.value.length === 0);

function selectAll() {
  selected.value = props.items;
}

function clear() {
  selected.value = [];
}

async function exportPNG() {
  if (selected.value.length === 0) {
    return;
  }

  if (props.supportIndividualExports) {
    emit('export', 'png', selected.value, () => (isExporting.value = false), oneFile.value);
    oneFile.value = false;
  } else {
    emit('export', 'png', selected.value, () => (isExporting.value = false));
  }
  selected.value = [];
  isExporting.value = true;
  toggleMenu();
}

function exportPDF() {
  if (selected.value.length === 0) {
    return;
  }

  if (props.supportIndividualExports) {
    emit('export', 'pdf', selected.value, () => (isExporting.value = false), oneFile.value);
    oneFile.value = false;
  } else {
    emit('export', 'pdf', selected.value, () => (isExporting.value = false));
  }
  selected.value = [];
  isExporting.value = true;
  toggleMenu();
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}
</script>

<template>
  <div class="tw:relative tw:flex tw:w-10 tw:flex-col" ref="dropdownTrigger">
    <span v-if="isExporting" class="tw:loading tw:loading-dots tw:loading-xs tw:self-center"></span>
    <div
      v-else
      role="button"
      tabindex="0"
      class="tw:btn tw:btn-xs tw:flex tw:h-7 tw:w-9 tw:self-center tw:border-none tw:bg-primary"
      :class="{'tw:btn-disabled' : props.disabled}"
      @click="toggleMenu">
      <Download :size="15" class="tw:stroke-white" />
    </div>
    <div
      v-if="menuOpen"
      class="tw:menu tw:menu-lg tw:absolute tw:bottom-auto tw:top-full tw:z-90 tw:mt-1 tw:flex tw:w-max tw:origin-top tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-base-100 tw:py-2 tw:shadow">
      <div v-for="(item, index) in props.items" :key="index" class="tw:flex tw:items-center tw:p-2">
        <input type="checkbox" v-model="selected" :value="item" :id="'checkbox-' + index" class="tw:mr-2" />
        <label :for="'checkbox-' + index" class="tw:flex-1">{{ item }}</label>
      </div>
      <div class="tw:flex" v-if="supportIndividualExports">
        <toggle id="one-file" label="Download As One File" v-model="oneFile" />
      </div>
      <div tabindex="0" class="tw:flex tw:justify-end tw:gap-2 tw:p-2">
        <button @click="selectAll" class="tw:btn tw:btn-secondary tw:btn-sm tw:text-white">Select All</button>
        <button @click="clear" class="tw:btn tw:btn-outline tw:btn-primary tw:btn-sm tw:border tw:border-solid">
          Clear
        </button>
        <button @click="exportPNG" :disabled="exportDisabled" class="tw:btn tw:btn-primary tw:btn-sm tw:text-white">
          Export PNG
        </button>
        <button @click="exportPDF" :disabled="exportDisabled" class="tw:btn tw:btn-primary tw:btn-sm tw:text-white">
          Export PDF
        </button>
      </div>
    </div>
  </div>
</template>
